import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
    Box,
    Checkbox,
    Chip,
    IconButton,
    ListSubheader,
    MenuItem,
    Select,
    Skeleton,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { alpha, useTheme } from '@mui/material/styles';
import * as d3 from 'd3';
import { useAssetPrices } from '../../api/hooks';
import type { CandleFrequency } from '../../api/hooks';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { PanelHeader, RangeBtn, SectionCard } from '../shared';
import {
    formatMetric,
    METRIC_KEYS,
    SORT_KEYS,
    SORT_LABELS,
    type IndicatorOption,
    type IndicatorSortKey,
} from '../../hooks/indicatorSeriesOptions';
import {
    indicatorBinaryColor,
    indicatorPalette,
    indicatorScale,
    metricTextColor,
} from './indicatorPalette';

import type { EventFromAsset, OHLCVPoint } from '../../api/types';
import type { MarketSeriesData } from '../../api/marketPricesClient';
import type { MarketLineMeta } from './convergence';
import {
    ClusteringOverlay,
    ClusterSeriesOption,
    COLORMAPS,
} from '../../hooks/useAssetClustering';
import type { IndicatorOverlay } from '../../hooks/useIndicatorOverlay';
import { ExportButton, type ExportSeries } from './ExportButton';
import { drawPercentAxis, drawAxisFitButton } from './chartAxis';
import { fitProbabilityDomain } from './chartYDomain';
import { useAxisFit } from '../../hooks/useAxisFit';

interface ClusterHoverInfo {
    label: string;
    color: string;
    returnPct: string;
    sharpe: string;
    returnColor: string;
}

interface IndicatorHoverInfo {
    color: string;
    pct: string;
}

interface ProbabilityHoverInfo {
    color: string;
    label: string;
    probPct: string;
}

interface HoverInfo {
    date: string;
    dateEnd: string;
    open: string;
    high: string;
    low: string;
    close: string;
    changePct: string;
    isGreen: boolean;
    cluster?: ClusterHoverInfo;
    indicator?: IndicatorHoverInfo;
    probabilities?: ProbabilityHoverInfo[];
}

export interface ProbabilityOverlay {
    series: MarketSeriesData[];
    allMarketMeta: MarketLineMeta[];
    visibleMarketIds: Set<string> | null; // null = lead market only
    onVisibleMarketsChange: (ids: Set<string> | null) => void;
    loading: boolean; // true while lead market is loading
    allLoaded: boolean; // true when every market has resolved
    eventStartDate: Date;
    eventEndDate: Date;
    eventTitle: string;
}

interface PriceChartProps {
    ticker?: string;
    assetType?: string | null;
    events?: EventFromAsset[];
    hoveredEvent?: EventFromAsset | null;
    timeRange: TimeRange;
    onTimeRangeChange: (r: TimeRange) => void;
    probabilityOverlay?: ProbabilityOverlay | null;
    zoomRange?: [Date, Date] | null;
    onZoomRangeChange?: (range: [Date, Date] | null) => void;
    clusteringOverlay?: ClusteringOverlay | null;
    clusteringAvailableSeries?: ClusterSeriesOption[];
    onClusteringChange?: (id: string) => void;
    clusteringSelectedId?: string | null;
    /** Selectable indicator series, rendered inside the same selector so there
     *  is one control for what the band shows. */
    indicatorOptions?: IndicatorOption[];
    indicatorSelectedSeries?: string;
    onIndicatorSeriesChange?: (series: string) => void;
    indicatorSortKey?: IndicatorSortKey;
    onIndicatorSortKeyChange?: (key: IndicatorSortKey) => void;
    indicatorOverlay?: IndicatorOverlay | null;
    onEventTitleClick?: () => void;
    newsMarkerDate?: Date | null;
    newsPinnedDates?: Date[];
    /**
     * When true, suppresses the SectionCard/PanelHeader wrapper and all
     * controls, rendering only the chart area. Used by EmbeddedAssetChart
     * which supplies its own header strip.
     */
    compact?: boolean;
    /**
     * Chart SVG height in px. Replaces the hardcoded 300. Has no effect on
     * fullscreen height (which is container-derived). Defaults to 300.
     */
    chartHeight?: number;
    /**
     * Pre-measured container width supplied by the parent (EmbeddedAssetChart).
     * In compact mode this is preferred over the internally-measured width
     * because the parent's ResizeObserver fires synchronously on layout
     * whereas the compact root element's ResizeObserver has an async gap
     * that can leave width=0 on the first D3 render.
     */
    containerWidth?: number;
}

const MARGIN = { top: 12, right: 44, bottom: 30 };
export const CHART_MARGIN_LEFT = 48;
const MARGIN_LEFT = CHART_MARGIN_LEFT;

export type TimeRange = '1W' | '1M' | '3M' | '1Y' | 'ALL';

/** Smart Y-axis label: $68.2k for large values, $256 for mid, $0.0034 for tiny */
function formatPrice(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 10_000) return `$${(value / 1_000).toFixed(1)}k`;
    if (abs >= 100) return `$${value.toFixed(0)}`;
    if (abs >= 1) return `$${value.toFixed(2)}`;
    if (abs >= 0.01) return `$${value.toFixed(4)}`;
    return `$${value.toPrecision(2)}`;
}

function formatPriceFull(value: number): string {
    return (
        '$' +
        value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
}

export function PriceChart({
    ticker,
    assetType,
    events,
    hoveredEvent: hoveredEventProp,
    timeRange,
    onTimeRangeChange,
    probabilityOverlay,
    zoomRange = null,
    onZoomRangeChange,
    clusteringOverlay,
    clusteringAvailableSeries,
    onClusteringChange,
    clusteringSelectedId,
    indicatorOptions,
    indicatorSelectedSeries,
    onIndicatorSeriesChange,
    indicatorSortKey = 'ret365d',
    onIndicatorSortKeyChange,
    indicatorOverlay,
    onEventTitleClick,
    newsMarkerDate = null,
    newsPinnedDates = [],
    compact = false,
    chartHeight,
    containerWidth,
}: PriceChartProps) {
    const theme = useTheme();
    const { ref: containerRef, width: internalWidth } = useContainerWidth();
    const svgRef = useRef<SVGSVGElement>(null);
    const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
    // Stable ref so D3 event handlers can call setHoverInfo without being in the effect dep array
    const setHoverInfoRef = useRef(setHoverInfo);

    const [binaryMode, setBinaryMode] = useState(true);
    const [fullscreen, setFullscreen] = useState(false);
    // null = auto-select based on available pixel width per candle
    const [frequencyOverride, setFrequencyOverride] =
        useState<CandleFrequency | null>(null);

    // Ref on the fullscreen outer wrapper Box (position:fixed; height:95vh).
    // We measure height from HERE rather than from containerRef (the chartArea that
    // contains the SVG) to avoid a feedback loop: if we measured containerRef, D3
    // setting the SVG tall would grow the flex item via min-height:auto, which would
    // make containerRef.clientHeight bigger, which would make D3 set the SVG even
    // taller on the next re-run, etc. — causing the "grows on every mouse move" bug.
    const fullscreenWrapperRef = useRef<HTMLDivElement>(null);

    // Derived SVG height in px. Updated once per fullscreen toggle via useLayoutEffect
    // so that React and D3 always agree on the height (no competing attr writes).
    // 80px ≈ PanelHeader (~48px) + chartArea py:1.5 padding (~24px) + buffer.
    const [svgHeight, setSvgHeight] = useState<number>(chartHeight ?? 300);
    useLayoutEffect(() => {
        if (!fullscreen || !fullscreenWrapperRef.current) {
            setSvgHeight(chartHeight ?? 300);
            return;
        }
        const wrapperH = fullscreenWrapperRef.current.clientHeight;
        setSvgHeight(Math.max(chartHeight ?? 300, wrapperH - 80));
    }, [fullscreen, chartHeight]);

    // In compact mode, prefer the externally measured width (supplied by
    // EmbeddedAssetChart's own useContainerWidth) over the internal measurement.
    // The internal measurement has a ResizeObserver timing gap when PriceChart
    // returns its containerRef Box as the compact root element — the observer
    // fires asynchronously, so if data also arrives at the same time React may
    // batch both updates and run the D3 effect only once with width still 0.
    const width =
        compact && containerWidth != null && containerWidth > 0
            ? containerWidth
            : internalWidth;

    // Reset override when timeRange changes so auto-select re-runs
    const prevTimeRangeRef = useRef(timeRange);
    if (prevTimeRangeRef.current !== timeRange) {
        prevTimeRangeRef.current = timeRange;
        if (frequencyOverride !== null) setFrequencyOverride(null);
    }

    // Pixels per candle at 1D determines whether we need to coarsen.
    // RANGE_DAYS is duplicated here to avoid a cross-file import of the const.
    const RANGE_DAYS_LOCAL: Record<TimeRange, number> = {
        '1W': 7,
        '1M': 30,
        '3M': 90,
        '1Y': 365,
        ALL: 365 * 15,
    };
    const chartWidth = width - CHART_MARGIN_LEFT - 44; // approximate usable px
    const pxPerDailyCandle = chartWidth / RANGE_DAYS_LOCAL[timeRange];
    // Only coarsen to 1W/1M on ALL range; for everything ≤ 1Y keep daily granularity
    const autoFrequency: CandleFrequency =
        pxPerDailyCandle > 40
            ? '1H'
            : timeRange !== 'ALL'
              ? '1D'
              : pxPerDailyCandle < 3
                ? '1M'
                : pxPerDailyCandle < 8
                  ? '1W'
                  : '1D';
    const frequency: CandleFrequency = frequencyOverride ?? autoFrequency;

    // Close fullscreen on Escape
    useEffect(() => {
        if (!fullscreen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [fullscreen]);

    // Whether the indicator sentinel is selected
    const indicatorSelected = clusteringSelectedId === '__indicator__';

    // Indicator entries carry their series in the option value, so one Select
    // covers "which overlay" and "which indicator series" at once.
    const INDICATOR_PREFIX = '__indicator__::';
    const selectValue = indicatorSelected
        ? `${INDICATOR_PREFIX}${indicatorSelectedSeries ?? ''}`
        : (clusteringSelectedId ?? '');

    const handleOverlayChange = (raw: string) => {
        if (raw.startsWith(INDICATOR_PREFIX)) {
            onIndicatorSeriesChange?.(raw.slice(INDICATOR_PREFIX.length));
            onClusteringChange?.('__indicator__');
            return;
        }
        onClusteringChange?.(raw);
    };

    const selectedIndicatorOption = indicatorOptions?.find(
        o => o.series === indicatorSelectedSeries
    );

    // A continuous dial needs the full colour ramp; an in/out switch reads as
    // green/red. Follow the selected series, but leave the switch usable.
    useEffect(() => {
        if (indicatorSelected && selectedIndicatorOption) {
            setBinaryMode(!selectedIndicatorOption.fractional);
        }
    }, [indicatorSelected, selectedIndicatorOption?.series]);

    // Color for indicator value [0,1] based on binaryMode
    // Uses the same COLORMAP endpoints as clustering
    const BINARY_NEGATIVE = COLORMAPS[2][0];
    const BINARY_POSITIVE = COLORMAPS[2][1];

    // The indicator band uses its own normed scale (sell / hold / buy) rather
    // than the clustering colormap, which ranks clusters worst->best and has no
    // buy/sell meaning. Clustering keeps COLORMAPS untouched.
    const isDarkMode = theme.palette.mode === 'dark';
    const indicatorRamp = useMemo(
        () => indicatorScale(isDarkMode),
        [isDarkMode]
    );

    function indicatorColor(value: number): string {
        return binaryMode
            ? indicatorBinaryColor(value, isDarkMode)
            : indicatorRamp(value);
    }

    const { data, loading, error } = useAssetPrices(
        ticker || null,
        assetType || null,
        timeRange,
        frequency
    );

    // Determine which market IDs are visible for the overlay
    const visibleSeries = useMemo(() => {
        if (
            !probabilityOverlay ||
            probabilityOverlay.loading ||
            probabilityOverlay.series.length === 0
        )
            return [];
        const { series, visibleMarketIds, allMarketMeta } = probabilityOverlay;
        if (visibleMarketIds === null) {
            // Lead market only (first conditionId)
            const leadId = allMarketMeta[0]?.conditionId;
            if (!leadId) return [];
            const found = series.find(s => s.conditionId === leadId);
            return found ? [found] : [];
        }
        return series.filter(s => visibleMarketIds.has(s.conditionId));
    }, [probabilityOverlay]);

    const hasOverlay =
        !!probabilityOverlay &&
        !probabilityOverlay.loading &&
        visibleSeries.length > 0;

    const { fit, toggleFit } = useAxisFit();

    // In binary mode collapse all clusters to red (negative return) or blue (positive),
    // using the two endpoints of the handcrafted COLORMAP_2.
    const effectiveClusterColors = useMemo(() => {
        if (!clusteringOverlay || !binaryMode)
            return clusteringOverlay?.clusterColors ?? {};
        const result: Record<number, string> = {};
        for (const [idStr] of Object.entries(clusteringOverlay.clusterColors)) {
            const id = Number(idStr);
            const mean = clusteringOverlay.clusterStats[id]?.mean ?? 0;
            result[id] = mean >= 0 ? BINARY_POSITIVE : BINARY_NEGATIVE;
        }
        return result;
    }, [clusteringOverlay, binaryMode, BINARY_NEGATIVE, BINARY_POSITIVE]);

    // Checked IDs for the market selector dropdown
    const checkedIds = useMemo(() => {
        if (!probabilityOverlay) return [];
        if (probabilityOverlay.visibleMarketIds === null) {
            const leadId = probabilityOverlay.allMarketMeta[0]?.conditionId;
            return leadId ? [leadId] : [];
        }
        return Array.from(probabilityOverlay.visibleMarketIds);
    }, [probabilityOverlay]);

    const exportSeries = useMemo((): ExportSeries[] => {
        const result: ExportSeries[] = [];

        // Asset price candle data
        if (data && data.length > 0) {
            result.push({
                label: ticker ?? 'asset',
                data: data,
                isPrice: true,
            });
        }

        // Probability overlay series (when an event is selected in AssetView)
        if (hasOverlay && probabilityOverlay) {
            for (const s of visibleSeries) {
                if (s.data.length === 0) continue;
                const meta = probabilityOverlay.allMarketMeta.find(
                    m => m.conditionId === s.conditionId
                );
                result.push({
                    label: meta?.question ?? s.conditionId,
                    data: s.data,
                });
            }
        }

        return result;
    }, [data, ticker, hasOverlay, probabilityOverlay, visibleSeries]);

    const handleMarketToggle = (event: SelectChangeEvent<string[]>) => {
        if (!probabilityOverlay) return;
        const val = event.target.value as string[];
        if (val.length === 0) {
            probabilityOverlay.onVisibleMarketsChange(null);
        } else {
            probabilityOverlay.onVisibleMarketsChange(new Set(val));
        }
    };

    // D3 render
    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        if (!data || data.length === 0) return;

        const w = width - MARGIN_LEFT - MARGIN.right;
        // svgHeight is pre-computed in a useLayoutEffect from the stable fullscreen
        // wrapper element, so we don't need to measure anything here.
        const height = svgHeight;
        const h = height - MARGIN.top - MARGIN.bottom;

        if (w <= 0 || h <= 0) return;

        // Keep SVG element height in sync — svgHeight is also used as the React
        // height prop, so this is redundant but kept for safety.
        d3.select(svgRef.current).attr('height', height);

        const isDark = theme.palette.mode === 'dark';
        const gridColor = isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06);
        const textColor = theme.palette.text.disabled;
        const lineColor = theme.palette.primary.main;

        // Scales
        const fullExtent = d3.extent(data, d => d.t) as [Date, Date];
        // Pad the right edge by one estimated candle interval so today's candle
        // and its overlay rect are fully visible and not clipped.
        const candleIntervalMs =
            data.length > 1
                ? (fullExtent[1].getTime() - fullExtent[0].getTime()) /
                  (data.length - 1)
                : 24 * 60 * 60 * 1000;
        const paddedEnd = new Date(fullExtent[1].getTime() + candleIntervalMs);
        const xDomain: [Date, Date] = zoomRange ?? [fullExtent[0], paddedEnd];
        const xScale = d3
            .scaleTime()
            .domain(xDomain)
            .range([MARGIN_LEFT, MARGIN_LEFT + w]);

        // Auto-fit y-axis to visible data when zoomed
        const visibleData = zoomRange
            ? data.filter(d => d.t >= zoomRange[0] && d.t <= zoomRange[1])
            : data;
        const yData = visibleData.length > 0 ? visibleData : data;
        const yMin = d3.min(yData, d => d.low) as number;
        const yMax = d3.max(yData, d => d.high) as number;
        const yPadding = (yMax - yMin) * 0.1;
        const yScale = d3
            .scaleLinear()
            .domain([yMin - yPadding, yMax + yPadding])
            .range([MARGIN.top + h, MARGIN.top]);

        // Probability Y scale — fitted to the event window's visible series
        const overlayValues: number[] = [];
        if (hasOverlay && probabilityOverlay) {
            const { eventStartDate, eventEndDate, allMarketMeta } =
                probabilityOverlay;
            for (const s of visibleSeries) {
                // Same guards the render applies below: a series with no meta
                // or fewer than two in-window points is never drawn, so it
                // must not widen the axis either.
                if (!allMarketMeta.some(m => m.conditionId === s.conditionId))
                    continue;
                const inWindow = s.data.filter(
                    d => d.t >= eventStartDate && d.t <= eventEndDate
                );
                if (inWindow.length < 2) continue;
                for (const d of inWindow) overlayValues.push(d.close);
            }
        }
        const probDomain = fitProbabilityDomain(overlayValues, fit);
        const probYScale = d3
            .scaleLinear()
            .domain(probDomain.domain)
            .range([MARGIN.top + h, MARGIN.top]);

        // Grid lines
        const yTicks = yScale.ticks(5);
        svg.append('g')
            .selectAll('line')
            .data(yTicks)
            .join('line')
            .attr('x1', MARGIN_LEFT)
            .attr('y1', d => yScale(d))
            .attr('x2', MARGIN_LEFT + w)
            .attr('y2', d => yScale(d))
            .attr('stroke', gridColor)
            .attr('stroke-width', 0.5);

        // Y axis labels (price - left side)
        svg.append('g')
            .selectAll('text')
            .data(yTicks)
            .join('text')
            .attr('x', MARGIN_LEFT - 4)
            .attr('y', d => yScale(d) + 3)
            .attr('fill', textColor)
            .attr('font-size', '9px')
            .attr('font-family', 'monospace')
            .attr('text-anchor', 'end')
            .text(d => formatPrice(d));

        // X axis
        const [tMin, tMax] = xScale.domain() as [Date, Date];
        const spanDays =
            (tMax.getTime() - tMin.getTime()) / (1000 * 60 * 60 * 24);
        const xFmt =
            spanDays > 365 ? d3.timeFormat("%b '%y") : d3.timeFormat('%b %d');
        const xTicks = xScale.ticks(5);
        svg.append('g')
            .selectAll('text')
            .data(xTicks)
            .join('text')
            .attr('x', d => xScale(d))
            .attr('y', height - 6)
            .attr('fill', textColor)
            .attr('font-size', '10px')
            .attr('text-anchor', 'middle')
            .text(d => xFmt(d));

        // Clip path for chart area
        const chartClipId =
            'price-chart-clip-' + Math.random().toString(36).slice(2, 8);
        const defs = svg.append('defs');
        defs.append('clipPath')
            .attr('id', chartClipId)
            .append('rect')
            .attr('x', MARGIN_LEFT)
            .attr('y', MARGIN.top)
            .attr('width', w)
            .attr('height', h);

        // Cluster / indicator fills — one rect per candle.
        // Top edge at yScale(min(open, close)); width spans to the next candle's x
        // (or one interval past the last candle so today is fully visible).
        const clusterOverlayOpacity = 0.33;
        const chartBottom = MARGIN.top + h;

        // Precompute per-candle right-edge x values (next candle's x, or +1 interval for last)
        const candleRightX = (idx: number): number => {
            const next = data[idx + 1];
            if (next) return xScale(next.t);
            const prev = data[idx - 1];
            if (prev)
                return (
                    xScale(data[idx]!.t) +
                    (xScale(data[idx]!.t) - xScale(prev.t))
                );
            return (
                xScale(data[idx]!.t) +
                candleIntervalMs /
                    ((xDomain[1].getTime() - xDomain[0].getTime()) / w)
            );
        };

        // Build a per-candle segment lookup by assigning each candle to the segment
        // whose start is the latest one <= d.t. This avoids boundary double-counting
        // and correctly handles candles that fall before the first explicit seg.start
        // (e.g. when the selected time range starts mid-segment).
        // The `d.t <= assigned.end` guard ensures candles beyond the last overlay
        // data point are not colored — extendSegments sets the last seg's end to the
        // last data point timestamp, so this naturally clips trailing candles.
        function buildCandleSegmentMap<T extends { start: Date; end: Date }>(
            segments: T[]
        ): Map<number, T> {
            if (!data) return new Map<number, T>();
            const sorted = [...segments].sort(
                (a, b) => a.start.getTime() - b.start.getTime()
            );
            const map = new Map<number, T>();
            data.forEach((d, i) => {
                let assigned: T | undefined;
                for (const seg of sorted) {
                    if (seg.start.getTime() <= d.t.getTime()) assigned = seg;
                    else break;
                }
                if (assigned && d.t.getTime() <= assigned.end.getTime())
                    map.set(i, assigned);
            });
            return map;
        }

        // Build staircase polygons for overlay fills.
        // Groups contiguous same-color candles into a single <path> so there are
        // no inter-candle boundaries at all — eliminating sub-pixel gap artifacts.
        // The top edge follows yScale(min(open, close)) per candle, producing the
        // same staircase shape as the previous per-rect approach.
        function renderOverlayPolygons(
            fills: Map<number, string>,
            opacity: number
        ) {
            if (!data) return;
            // Collect contiguous runs of identical fill color
            const runs: Array<{ fill: string; indices: number[] }> = [];
            data.forEach((_, i) => {
                const fill = fills.get(i);
                if (!fill) return;
                const last = runs[runs.length - 1];
                if (
                    last &&
                    last.fill === fill &&
                    last.indices[last.indices.length - 1] === i - 1
                ) {
                    last.indices.push(i);
                } else {
                    runs.push({ fill, indices: [i] });
                }
            });

            for (const run of runs) {
                const pts: [number, number][] = [];
                const first = run.indices[0]!;
                const last = run.indices[run.indices.length - 1]!;

                pts.push([xScale(data[first]!.t), chartBottom]);
                pts.push([
                    xScale(data[first]!.t),
                    yScale(Math.min(data[first]!.open, data[first]!.close)),
                ]);

                for (let k = 0; k < run.indices.length; k++) {
                    const i = run.indices[k]!;
                    const top = yScale(Math.min(data[i]!.open, data[i]!.close));
                    const x2 = candleRightX(i);
                    pts.push([x2, top]);
                    // Step vertically to next candle's top at the same x
                    if (k < run.indices.length - 1) {
                        const nextTop = yScale(
                            Math.min(
                                data[run.indices[k + 1]!]!.open,
                                data[run.indices[k + 1]!]!.close
                            )
                        );
                        pts.push([x2, nextTop]);
                    }
                }

                pts.push([candleRightX(last), chartBottom]);

                const d = 'M ' + pts.map(p => p.join(' ')).join(' L ') + ' Z';
                svg.append('path')
                    .attr('d', d)
                    .attr('fill', run.fill)
                    .attr('opacity', opacity)
                    .attr('clip-path', `url(#${chartClipId})`);
            }
        }

        if (clusteringOverlay && clusteringOverlay.segments.length > 0) {
            const candleSegMap = buildCandleSegmentMap(
                clusteringOverlay.segments
            );
            const fills = new Map<number, string>();
            data.forEach((_, i) => {
                const seg = candleSegMap.get(i);
                if (seg)
                    fills.set(
                        i,
                        effectiveClusterColors[seg.clusterId] ?? '#888'
                    );
            });
            renderOverlayPolygons(fills, clusterOverlayOpacity);
        }

        // Indicator fills — same staircase polygon approach
        if (
            indicatorSelected &&
            indicatorOverlay &&
            indicatorOverlay.segments.length > 0
        ) {
            const candleSegMap = buildCandleSegmentMap(
                indicatorOverlay.segments
            );
            const fills = new Map<number, string>();
            data.forEach((_, i) => {
                const seg = candleSegMap.get(i);
                if (seg) fills.set(i, indicatorColor(seg.value));
            });
            renderOverlayPolygons(fills, clusterOverlayOpacity);
        }

        // Candlesticks
        const candleUp = isDark ? '#26a69a' : '#089981';
        const candleDown = isDark ? '#ef5350' : '#f23645';

        // Derive slot width from the actual time spacing so candles align with
        // the x-axis and with cluster/indicator fills (which also use d.t as left edge).
        // Median step is more robust than first-pair diff when data has gaps.
        const steps = visibleData
            .slice(1)
            .map((d, i) => xScale(d.t) - xScale(visibleData[i]!.t));
        const slotPx =
            steps.length > 0
                ? steps.slice().sort((a, b) => a - b)[
                      Math.floor(steps.length / 2)
                  ]!
                : w / Math.max(1, visibleData.length);

        // 0.05 gap ratio → very tight spacing
        const GAP = 0.05;
        const candleWidth = Math.max(1, slotPx * (1 - GAP));
        // small left inset to centre the gap evenly on both sides
        const bodyOffset = (slotPx * GAP) / 2;

        const candleGroup = svg
            .append('g')
            .attr('clip-path', `url(#${chartClipId})`);

        // Wicks (high → low) — centred within the body slot
        candleGroup
            .selectAll('line.wick')
            .data(visibleData)
            .join('line')
            .attr('class', 'wick')
            .attr('x1', d => xScale(d.t) + bodyOffset + candleWidth / 2)
            .attr('x2', d => xScale(d.t) + bodyOffset + candleWidth / 2)
            .attr('y1', d => yScale(d.high))
            .attr('y2', d => yScale(d.low))
            .attr('stroke', d => (d.close >= d.open ? candleUp : candleDown))
            .attr('stroke-width', Math.min(2, Math.max(1, candleWidth * 0.15)));

        // Bodies — left edge at xScale(d.t) so they align with cluster/indicator fills
        candleGroup
            .selectAll('rect.body')
            .data(visibleData)
            .join('rect')
            .attr('class', 'body')
            .attr('x', d => xScale(d.t) + bodyOffset)
            .attr('y', d => yScale(Math.max(d.open, d.close)))
            .attr('width', candleWidth)
            // Minimum 1px height so doji candles are still visible
            .attr('height', d =>
                Math.max(1, Math.abs(yScale(d.open) - yScale(d.close)))
            )
            .attr('fill', d => (d.close >= d.open ? candleUp : candleDown));

        // Period-start reference line + end marker.
        // Dashed line at refY; pill card at right edge of last candle at closeY.
        const refCandle = visibleData[0];
        const lastCandle = visibleData[visibleData.length - 1];
        if (refCandle && lastCandle && refCandle !== lastCandle) {
            const refPrice = refCandle.open;
            const lastClose = lastCandle.close;
            const refY = yScale(refPrice);
            const closeY = yScale(lastClose);
            const pctChange = ((lastClose - refPrice) / refPrice) * 100;
            const isPositive = pctChange >= 0;
            const markerColor = isPositive
                ? isDark
                    ? '#26a69a'
                    : '#089981'
                : isDark
                  ? '#ef5350'
                  : '#f23645';

            // Dashed horizontal reference line — neutral so it doesn't imply direction
            svg.append('line')
                .attr('x1', MARGIN_LEFT)
                .attr('y1', refY)
                .attr('x2', MARGIN_LEFT + w)
                .attr('y2', refY)
                .attr(
                    'stroke',
                    isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)'
                )
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '4 3')
                .attr('opacity', 0.5)
                .attr('clip-path', `url(#${chartClipId})`);

            // No decimal when abs value >= 10, one decimal otherwise
            const decimals = Math.abs(pctChange) >= 10 ? 0 : 1;
            const labelText = `${isPositive ? '+' : ''}${pctChange.toFixed(decimals)}%`;
            const pillH = 16;
            const pillPad = 5;
            // ~6px per char at 10px monospace
            const pillW = labelText.length * 6 + pillPad * 2;
            const lastIdx = data.indexOf(lastCandle);
            // Clamp so the pill never overflows the SVG right edge
            const pillX = Math.min(
                candleRightX(lastIdx) + 4,
                width - pillW - 2
            );
            const pillY = closeY - pillH / 2;

            svg.append('rect')
                .attr('x', pillX)
                .attr('y', pillY)
                .attr('width', pillW)
                .attr('height', pillH)
                .attr('rx', 3)
                .attr('fill', markerColor)
                .attr('opacity', 0.85);

            svg.append('text')
                .attr('x', pillX + pillPad)
                .attr('y', closeY + 4)
                .attr('fill', '#fff')
                .attr('font-size', '10px')
                .attr('font-family', 'monospace')
                .attr('font-weight', '600')
                .text(labelText);
        }

        // Event markers - only for the active (hovered or selected) event
        const activeEvent =
            hoveredEventProp ??
            (probabilityOverlay
                ? events?.find(e => {
                      const s = new Date(e.start_date).getTime();
                      return s === probabilityOverlay.eventStartDate.getTime();
                  })
                : null);
        if (activeEvent) {
            const mColor = theme.palette.text.secondary;
            const eventGroup = svg.append('g').attr('class', 'event-markers');
            const [domainStart, domainEnd] = xScale.domain() as [Date, Date];

            const startD = new Date(activeEvent.start_date);
            const endD = new Date(activeEvent.end_date);
            const startInView = startD >= domainStart && startD <= domainEnd;
            const endInView = endD >= domainStart && endD <= domainEnd;

            // Highlight area between start and end
            const hlX1 = Math.max(MARGIN_LEFT, xScale(startD));
            const hlX2 = Math.min(MARGIN_LEFT + w, xScale(endD));
            if (hlX2 > hlX1) {
                eventGroup
                    .append('rect')
                    .attr('x', hlX1)
                    .attr('y', MARGIN.top)
                    .attr('width', hlX2 - hlX1)
                    .attr('height', h)
                    .attr('fill', isDark ? '#fff' : '#fff')
                    .attr('opacity', 0.03);
            }

            for (const dateStr of [
                activeEvent.start_date,
                activeEvent.end_date,
            ]) {
                if (!dateStr) continue;
                const d = new Date(dateStr);
                if (d >= domainStart && d <= domainEnd) {
                    eventGroup
                        .append('line')
                        .attr('x1', xScale(d))
                        .attr('y1', MARGIN.top)
                        .attr('x2', xScale(d))
                        .attr('y2', MARGIN.top + h)
                        .attr('stroke', mColor)
                        .attr('stroke-width', 1.5)
                        .attr('stroke-dasharray', '4 3')
                        .attr('opacity', 0.6);
                }
            }
        }

        // News article markers (pinned + hover)
        {
            const [domStart, domEnd] = xScale.domain() as [Date, Date];
            const pinnedSet = new Set(newsPinnedDates.map(d => d.getTime()));

            for (const d of newsPinnedDates) {
                if (d >= domStart && d <= domEnd) {
                    svg.append('line')
                        .attr('x1', xScale(d))
                        .attr('y1', MARGIN.top)
                        .attr('x2', xScale(d))
                        .attr('y2', MARGIN.top + h)
                        .attr('stroke', theme.palette.info.main)
                        .attr('stroke-width', 2);
                }
            }

            if (
                newsMarkerDate &&
                newsMarkerDate >= domStart &&
                newsMarkerDate <= domEnd &&
                !pinnedSet.has(newsMarkerDate.getTime())
            ) {
                svg.append('line')
                    .attr('x1', xScale(newsMarkerDate))
                    .attr('y1', MARGIN.top)
                    .attr('x2', xScale(newsMarkerDate))
                    .attr('y2', MARGIN.top + h)
                    .attr('stroke', theme.palette.info.main)
                    .attr('stroke-width', 1.5)
                    .attr('stroke-dasharray', '4 3')
                    .attr('opacity', 0.7);
            }
        }

        // Probability overlay - left Y axis and dashed lines
        if (hasOverlay && probabilityOverlay) {
            const { eventStartDate, eventEndDate, allMarketMeta } =
                probabilityOverlay;

            // Probability grid lines (dotted, subtle) + right axis labels
            drawPercentAxis(svg, {
                ticks: probDomain.ticks,
                y: v => probYScale(v),
                x1: MARGIN_LEFT,
                x2: MARGIN_LEFT + w,
                labelSide: 'right',
                gridColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.04),
                textColor: isDark ? alpha('#fff', 0.4) : alpha('#000', 0.35),
                fontSize: '9px',
                dashArray: '2 3',
                decimals: probDomain.decimals,
            });
            drawAxisFitButton(svg, {
                x: MARGIN_LEFT + w + 4,
                y: MARGIN.top - 26,
                fit,
                accentColor: theme.palette.primary.main,
                mutedColor: isDark ? alpha('#fff', 0.4) : alpha('#000', 0.35),
                onToggle: toggleFit,
            });

            // Clip path for event date range
            const clipId = 'prob-clip';
            const clipX1 = Math.max(MARGIN_LEFT, xScale(eventStartDate));
            const clipX2 = Math.min(MARGIN_LEFT + w, xScale(eventEndDate));
            defs.append('clipPath')
                .attr('id', clipId)
                .append('rect')
                .attr('x', clipX1)
                .attr('y', MARGIN.top)
                .attr('width', Math.max(0, clipX2 - clipX1))
                .attr('height', h);

            // Render probability lines
            for (const s of visibleSeries) {
                const meta = allMarketMeta.find(
                    m => m.conditionId === s.conditionId
                );
                if (!meta) continue;

                const filteredData = s.data.filter(
                    d => d.t >= eventStartDate && d.t <= eventEndDate
                );
                if (filteredData.length < 2) continue;

                const probLine = d3
                    .line<OHLCVPoint>()
                    .x(d => xScale(d.t))
                    .y(d => probYScale(d.close));

                svg.append('path')
                    .datum(filteredData)
                    .attr('d', probLine)
                    .attr('fill', 'none')
                    .attr('stroke', meta.color)
                    .attr('stroke-width', 1.8)
                    .attr('stroke-dasharray', '6 3')
                    .attr('clip-path', `url(#${clipId})`);
            }
        }

        // Crosshair + info bar
        const crosshairGroup = svg.append('g').style('display', 'none');
        const crosshairVTop = crosshairGroup
            .append('line')
            .attr('stroke', textColor)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3 3');
        const crosshairVBottom = crosshairGroup
            .append('line')
            .attr('stroke', textColor)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3 3');
        const crosshairHLeft = crosshairGroup
            .append('line')
            .attr('stroke', textColor)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3 3');
        const crosshairHRight = crosshairGroup
            .append('line')
            .attr('stroke', textColor)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3 3');
        // Body outline + upper/lower wicks — always shown
        let crosshairCandleOutlineColor = isDark
            ? 'rgba(255,255,255,0.9)'
            : 'rgba(0,0,0,0.9)';
        const crosshairRect = crosshairGroup
            .append('rect')
            .attr('stroke-width', 2)
            .attr('stroke', crosshairCandleOutlineColor)
            .attr('fill', textColor)
            .attr('fill-opacity', 0.5);
        const crosshairWickUpper = crosshairGroup
            .append('line')
            .attr('stroke', crosshairCandleOutlineColor)
            .attr('stroke-width', 2);
        const crosshairWickLower = crosshairGroup
            .append('line')
            .attr('stroke', crosshairCandleOutlineColor)
            .attr('stroke-width', 2);

        const bisect = d3.bisector<OHLCVPoint, Date>(d => d.t).left;

        // Brush-to-zoom
        const brush = d3
            .brushX()
            .extent([
                [MARGIN_LEFT, MARGIN.top],
                [MARGIN_LEFT + w, MARGIN.top + h],
            ])
            .on('end', event => {
                if (!event.sourceEvent) return;
                if (!event.selection) return;
                const [x0, x1] = event.selection as [number, number];
                const d0 = xScale.invert(x0);
                const d1 = xScale.invert(x1);
                brushGroup.call(brush.move, null);
                onZoomRangeChange?.([d0, d1]);
            });

        const brushGroup = svg.append('g').attr('class', 'brush').call(brush);

        brushGroup
            .selectAll('.selection')
            .attr('fill', alpha(theme.palette.primary.main, 0.12))
            .attr('stroke', alpha(theme.palette.primary.main, 0.4))
            .attr('stroke-width', 1);

        // Attach crosshair/tooltip to brush overlay
        brushGroup
            .select('.overlay')
            .style('cursor', 'crosshair')
            .on('mouseenter', () => {
                crosshairGroup.style('display', null);
            })
            .on('mouseleave', () => {
                crosshairGroup.style('display', 'none');
                setHoverInfoRef.current(null);
            })
            .on('mousemove', (event: MouseEvent) => {
                const [mx] = d3.pointer(event);
                const date = xScale.invert(mx);
                const idx = bisect(data, date, 1);
                const d0 = data[idx - 1];
                const d1 = data[idx];
                if (!d0) return;
                const d0Center = d0.t.getTime() + candleIntervalMs / 2;
                const d1Center = d1
                    ? d1.t.getTime() + candleIntervalMs / 2
                    : Infinity;
                const d =
                    Math.abs(date.getTime() - d0Center) <=
                    Math.abs(date.getTime() - d1Center)
                        ? d0
                        : d1!;

                const cx = xScale(d.t) + bodyOffset + candleWidth / 2;
                const cy = yScale((d.open + d.close) / 2);

                const bodyTop = yScale(Math.max(d.open, d.close));
                const bodyH = Math.max(
                    1,
                    Math.abs(yScale(d.open) - yScale(d.close))
                );

                crosshairRect
                    .attr('x', xScale(d.t) + bodyOffset)
                    .attr('y', bodyTop)
                    .attr('width', candleWidth)
                    .attr('height', bodyH)
                    .attr('rx', Math.min(2, candleWidth * 0.1));

                const wickWidth = Math.min(2, Math.max(1, candleWidth * 0.15));

                crosshairWickUpper
                    .attr('x1', cx)
                    .attr('x2', cx)
                    .attr('y1', yScale(d.high))
                    .attr('y2', bodyTop)
                    .attr('stroke-width', wickWidth);

                crosshairWickLower
                    .attr('x1', cx)
                    .attr('x2', cx)
                    .attr('y1', bodyTop + bodyH)
                    .attr('y2', yScale(d.low))
                    .attr('stroke-width', wickWidth);

                crosshairVTop
                    .attr('x1', cx)
                    .attr('x2', cx)
                    .attr('y1', MARGIN.top)
                    .attr('y2', yScale(d.high));
                crosshairVBottom
                    .attr('x1', cx)
                    .attr('x2', cx)
                    .attr('y1', yScale(d.low))
                    .attr('y2', MARGIN.top + h);
                crosshairHLeft
                    .attr('x1', MARGIN_LEFT)
                    .attr('x2', xScale(d.t) + bodyOffset)
                    .attr('y1', cy)
                    .attr('y2', cy);
                crosshairHRight
                    .attr('x1', xScale(d.t) + bodyOffset + candleWidth)
                    .attr('x2', MARGIN_LEFT + w)
                    .attr('y1', cy)
                    .attr('y2', cy);

                const candleEnd = new Date(d.t.getTime() + candleIntervalMs);
                const timeFmt = d3.timeFormat('%b %d, %Y %H:%M');

                const changeFromOpen = d.close - d.open;
                const changePct =
                    d.open !== 0
                        ? ((changeFromOpen / d.open) * 100).toFixed(2)
                        : '0.00';
                const isGreen = changeFromOpen >= 0;

                const info: HoverInfo = {
                    date: timeFmt(d.t),
                    dateEnd: timeFmt(candleEnd),
                    open: formatPriceFull(d.open),
                    high: formatPriceFull(d.high),
                    low: formatPriceFull(d.low),
                    close: formatPriceFull(d.close),
                    changePct,
                    isGreen,
                };

                // Cluster info
                if (clusteringOverlay) {
                    const seg = clusteringOverlay.segments.find(
                        s => d.t >= s.start && d.t < s.end
                    );
                    if (seg !== undefined) {
                        const color =
                            effectiveClusterColors[seg.clusterId] ?? '#888';
                        const stats =
                            clusteringOverlay.clusterStats[seg.clusterId];
                        const rank = clusteringOverlay.sortedClusterIds.indexOf(
                            seg.clusterId
                        );
                        const label =
                            rank >= 0
                                ? `Cluster ${rank + 1}`
                                : `Cluster ${seg.clusterId}`;
                        const returnPct = stats
                            ? (stats.mean * 100).toFixed(3)
                            : 'n/a';
                        const sharpe = stats ? stats.sharpe.toFixed(2) : 'n/a';
                        const returnColor =
                            stats && stats.mean >= 0 ? '#5cb85c' : '#e05c5c';
                        info.cluster = {
                            label,
                            color,
                            returnPct,
                            sharpe,
                            returnColor,
                        };
                    }
                }

                // Indicator info
                if (indicatorSelected && indicatorOverlay) {
                    const seg = indicatorOverlay.segments.find(
                        s => d.t >= s.start && d.t < s.end
                    );
                    if (seg !== undefined) {
                        info.indicator = {
                            color: indicatorColor(seg.value),
                            pct: (seg.value * 100).toFixed(1),
                        };
                    }
                }

                // Probability info
                if (hasOverlay && probabilityOverlay) {
                    const { eventStartDate, eventEndDate, allMarketMeta } =
                        probabilityOverlay;
                    if (d.t >= eventStartDate && d.t <= eventEndDate) {
                        const probs: ProbabilityHoverInfo[] = [];
                        for (const s of visibleSeries) {
                            const meta = allMarketMeta.find(
                                m => m.conditionId === s.conditionId
                            );
                            if (!meta || s.data.length === 0) continue;
                            const probBisect = d3.bisector<OHLCVPoint, Date>(
                                pt => pt.t
                            ).left;
                            const pi = probBisect(s.data, d.t, 1);
                            const p0 = s.data[pi - 1];
                            const p1 = s.data[pi];
                            if (!p0) continue;
                            const pd =
                                p1 &&
                                d.t.getTime() - p0.t.getTime() >
                                    p1.t.getTime() - d.t.getTime()
                                    ? p1
                                    : p0;
                            const label =
                                meta.question.length > 35
                                    ? meta.question.slice(0, 34) + '\u2026'
                                    : meta.question;
                            probs.push({
                                color: meta.color,
                                label,
                                probPct: (pd.close * 100).toFixed(1),
                            });
                        }
                        if (probs.length > 0) info.probabilities = probs;
                    }
                }

                setHoverInfoRef.current(info);
            });
    }, [
        data,
        width,
        theme,
        events,
        hoveredEventProp,
        hasOverlay,
        probabilityOverlay,
        visibleSeries,
        zoomRange,
        onZoomRangeChange,
        clusteringOverlay,
        effectiveClusterColors,
        indicatorOverlay,
        indicatorSelected,
        binaryMode,
        fullscreen,
        frequency,
        newsMarkerDate,
        newsPinnedDates,
        compact,
        svgHeight,
        fit,
        toggleFit,
    ]);

    // Show the loading skeleton whenever we have no drawable data:
    // - explicitly loading (no data yet), OR
    // - data came back as an empty array (no candles for this period/ticker).
    // The second case would otherwise render a blank SVG because D3 bails at
    // `data.length === 0` — showing the skeleton keeps the area from going dark.
    const showSkeleton =
        (loading && !data) ||
        (!loading && !error && Array.isArray(data) && data.length === 0);

    // Extracted so it can be returned directly in compact mode without
    // duplicating the hover info JSX.
    const chartArea = (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                px: 1,
                py: 1.5,
                flex: !compact && fullscreen ? 1 : undefined,
                overflow: 'visible',
            }}
        >
            {showSkeleton ? (
                <Skeleton
                    variant="rounded"
                    width="100%"
                    height={chartHeight ?? 300}
                    sx={{ borderRadius: '6px' }}
                />
            ) : error && !data ? (
                <Typography
                    sx={{
                        color: 'error.main',
                        textAlign: 'center',
                        py: 8,
                        fontSize: '13px',
                    }}
                >
                    Failed to load price data
                </Typography>
            ) : (
                <>
                    {/* Loading shimmer on right axis area */}
                    {probabilityOverlay && probabilityOverlay.loading && (
                        <Skeleton
                            variant="rectangular"
                            width={36}
                            height={258}
                            sx={{
                                position: 'absolute',
                                right: 12,
                                top: 20,
                                borderRadius: '4px',
                                zIndex: 5,
                            }}
                        />
                    )}
                    <svg
                        ref={svgRef}
                        width={width}
                        height={svgHeight}
                        style={{ display: 'block' }}
                    />
                    {hoverInfo && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 6,
                                left: MARGIN_LEFT + 4,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '2px 10px',
                                pointerEvents: 'none',
                                zIndex: 10,
                                lineHeight: 1,
                            }}
                        >
                            {/* Date range */}
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.disabled',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {hoverInfo.date}
                                {hoverInfo.dateEnd !== hoverInfo.date && (
                                    <> &rarr; {hoverInfo.dateEnd}</>
                                )}
                            </Typography>

                            {/* O H L C */}
                            {(
                                [
                                    {
                                        label: 'open',
                                        value: hoverInfo.open,
                                        color: 'text.primary',
                                    },
                                    {
                                        label: 'high',
                                        value: hoverInfo.high,
                                        color: '#22c55e',
                                    },
                                    {
                                        label: 'low',
                                        value: hoverInfo.low,
                                        color: '#ef4444',
                                    },
                                    {
                                        label: 'close',
                                        value: hoverInfo.close,
                                        color: hoverInfo.isGreen
                                            ? '#22c55e'
                                            : '#ef4444',
                                    },
                                ] as const
                            ).map(({ label, value, color }) => (
                                <Box
                                    key={label}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: '3px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.disabled',
                                            fontWeight: 500,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            fontFamily: 'monospace',
                                            color,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            ))}

                            {/* % change */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '3px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        color: 'text.disabled',
                                        fontWeight: 500,
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    change
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        fontFamily: 'monospace',
                                        color: hoverInfo.isGreen
                                            ? '#22c55e'
                                            : '#ef4444',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {hoverInfo.isGreen ? '+' : ''}
                                    {hoverInfo.changePct}%
                                </Typography>
                            </Box>

                            {/* Separator before overlay info */}
                            {(hoverInfo.cluster ||
                                hoverInfo.indicator ||
                                (hoverInfo.probabilities &&
                                    hoverInfo.probabilities.length > 0)) && (
                                <Box
                                    sx={{
                                        width: '1px',
                                        height: '14px',
                                        bgcolor: 'divider',
                                        flexShrink: 0,
                                        alignSelf: 'center',
                                    }}
                                />
                            )}

                            {/* Cluster */}
                            {hoverInfo.cluster && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '2px',
                                            bgcolor: hoverInfo.cluster.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {hoverInfo.cluster.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            color: hoverInfo.cluster
                                                .returnColor,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {hoverInfo.cluster.returnPct}%
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Sharpe&nbsp;
                                        <Box
                                            component="span"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 600,
                                                color: 'text.primary',
                                            }}
                                        >
                                            {hoverInfo.cluster.sharpe}
                                        </Box>
                                    </Typography>
                                </Box>
                            )}

                            {/* Indicator */}
                            {hoverInfo.indicator && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '2px',
                                            bgcolor: hoverInfo.indicator.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Indicator
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            color: 'text.primary',
                                        }}
                                    >
                                        {hoverInfo.indicator.pct}%
                                    </Typography>
                                </Box>
                            )}

                            {/* Probabilities */}
                            {hoverInfo.probabilities?.map((p, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: p.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            whiteSpace: 'nowrap',
                                            maxWidth: 160,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {p.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            color: p.color,
                                        }}
                                    >
                                        {p.probPct}%
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );

    if (compact) {
        return chartArea;
    }

    return (
        <>
            {fullscreen && (
                <Box
                    onClick={() => setFullscreen(false)}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        zIndex: 1299,
                    }}
                />
            )}
            <Box
                ref={fullscreenWrapperRef}
                sx={
                    fullscreen
                        ? {
                              position: 'fixed',
                              top: '2.5vh',
                              left: '2.5vw',
                              width: '95vw',
                              height: '95vh',
                              zIndex: 1300,
                          }
                        : undefined
                }
            >
                <SectionCard
                    sx={{
                        mb: fullscreen ? 0 : undefined,
                        height: fullscreen ? '100%' : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible',
                    }}
                >
                    <PanelHeader title={`${ticker || 'Asset'} / USD`}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            {probabilityOverlay && (
                                <>
                                    <Typography
                                        component="span"
                                        onClick={onEventTitleClick}
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            cursor: onEventTitleClick
                                                ? 'pointer'
                                                : 'default',
                                            '&:hover': onEventTitleClick
                                                ? {
                                                      textDecoration:
                                                          'underline',
                                                  }
                                                : undefined,
                                        }}
                                    >
                                        {probabilityOverlay.eventTitle}
                                    </Typography>
                                    <Select
                                        multiple
                                        value={checkedIds}
                                        onChange={handleMarketToggle}
                                        renderValue={selected =>
                                            `${(selected as string[]).length} market${(selected as string[]).length > 1 ? 's' : ''}`
                                        }
                                        size="small"
                                        sx={{
                                            height: 26,
                                            fontSize: '11px',
                                            maxWidth: 180,
                                            '& .MuiSelect-select': {
                                                py: 0,
                                                pl: 1,
                                            },
                                        }}
                                    >
                                        {probabilityOverlay.allMarketMeta.map(
                                            m => (
                                                <MenuItem
                                                    key={m.conditionId}
                                                    value={m.conditionId}
                                                    dense
                                                >
                                                    <Checkbox
                                                        checked={checkedIds.includes(
                                                            m.conditionId
                                                        )}
                                                        size="small"
                                                        sx={{
                                                            p: 0.25,
                                                            mr: 0.5,
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: m.color,
                                                            mr: 0.5,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize: '12px',
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                    >
                                                        {m.question}
                                                    </Typography>
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </>
                            )}
                            {((clusteringAvailableSeries &&
                                clusteringAvailableSeries.length > 0) ||
                                indicatorOverlay) &&
                                onClusteringChange && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Select
                                            value={selectValue}
                                            onChange={e =>
                                                handleOverlayChange(
                                                    e.target.value as string
                                                )
                                            }
                                            size="small"
                                            renderValue={v =>
                                                String(v).startsWith(
                                                    INDICATOR_PREFIX
                                                )
                                                    ? (selectedIndicatorOption?.name ??
                                                      'Indicator')
                                                    : (clusteringAvailableSeries?.find(
                                                          s => s.id === v
                                                      )?.name ??
                                                      (v === '__none__'
                                                          ? 'None'
                                                          : String(v)))
                                            }
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: { maxHeight: 460 },
                                                },
                                            }}
                                            sx={{
                                                height: 26,
                                                fontSize: '11px',
                                                maxWidth: 260,
                                                '& .MuiSelect-select': {
                                                    py: 0,
                                                    pl: 1,
                                                },
                                            }}
                                        >
                                            <MenuItem value="__none__" dense>
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: 'text.disabled',
                                                    }}
                                                >
                                                    None
                                                </Typography>
                                            </MenuItem>
                                            {indicatorOverlay &&
                                                indicatorOptions &&
                                                indicatorOptions.length > 0 && [
                                                    <ListSubheader
                                                        key="__ind_head__"
                                                        sx={{
                                                            lineHeight: '28px',
                                                            fontSize: '11px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <span>Indicator</span>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 0.5,
                                                                ml: 'auto',
                                                            }}
                                                        >
                                                            {SORT_KEYS.map(k => (
                                                                <Box
                                                                    component="span"
                                                                    key={k}
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        onIndicatorSortKeyChange?.(
                                                                            k
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        px: 0.6,
                                                                        borderRadius: 0.5,
                                                                        fontSize: '10px',
                                                                        fontWeight:
                                                                            indicatorSortKey ===
                                                                            k
                                                                                ? 700
                                                                                : 400,
                                                                        color:
                                                                            indicatorSortKey ===
                                                                            k
                                                                                ? 'primary.main'
                                                                                : 'text.secondary',
                                                                        bgcolor:
                                                                            indicatorSortKey ===
                                                                            k
                                                                                ? 'action.selected'
                                                                                : 'transparent',
                                                                    }}
                                                                >
                                                                    {SORT_LABELS[k]}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </ListSubheader>,
                                                    ...indicatorOptions.map(o => (
                                                        <MenuItem
                                                            key={o.series}
                                                            value={`${INDICATOR_PREFIX}${o.series}`}
                                                            dense
                                                            sx={
                                                                o.isDefault
                                                                    ? {
                                                                          bgcolor:
                                                                              'action.hover',
                                                                          borderLeft: 2,
                                                                          borderColor:
                                                                              'primary.main',
                                                                      }
                                                                    : undefined
                                                            }
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    gap: 1.5,
                                                                    width: '100%',
                                                                    alignItems:
                                                                        'baseline',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            '12px',
                                                                        fontWeight:
                                                                            o.isDefault
                                                                                ? 600
                                                                                : 400,
                                                                        overflow:
                                                                            'hidden',
                                                                        textOverflow:
                                                                            'ellipsis',
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                    }}
                                                                >
                                                                    {o.name}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        gap: 1.5,
                                                                        ml: 'auto',
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                    }}
                                                                >
                                                                    {METRIC_KEYS.map(
                                                                        key => {
                                                                            // Same list, same order as the
                                                                            // header toggles. Marking the
                                                                            // active column keeps a big
                                                                            // negative in another one from
                                                                            // reading as a broken sort.
                                                                            const label =
                                                                                SORT_LABELS[
                                                                                    key
                                                                                ];
                                                                            const text =
                                                                                formatMetric(
                                                                                    o,
                                                                                    key
                                                                                );
                                                                            const active =
                                                                                indicatorSortKey ===
                                                                                key;
                                                                            // Sign carries the meaning;
                                                                            // colour reinforces it. Weight,
                                                                            // not hue, marks the sort column
                                                                            // so the two never compete.
                                                                            const signColor =
                                                                                metricTextColor(
                                                                                    o[key],
                                                                                    isDarkMode
                                                                                );
                                                                            return (
                                                                                <Typography
                                                                                    key={
                                                                                        key
                                                                                    }
                                                                                    sx={{
                                                                                        fontSize:
                                                                                            '11px',
                                                                                        color:
                                                                                            signColor ??
                                                                                            'text.disabled',
                                                                                        fontWeight:
                                                                                            active
                                                                                                ? 700
                                                                                                : 400,
                                                                                        fontVariantNumeric:
                                                                                            'tabular-nums',
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'text.disabled',
                                                                                            mr: 0.4,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            label
                                                                                        }
                                                                                    </Box>
                                                                                    {
                                                                                        text
                                                                                    }
                                                                                </Typography>
                                                                            );
                                                                        }
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </MenuItem>
                                                    )),
                                                    <ListSubheader
                                                        key="__clu_head__"
                                                        sx={{
                                                            lineHeight: '28px',
                                                            fontSize: '11px',
                                                        }}
                                                    >
                                                        Clustering
                                                    </ListSubheader>,
                                                ]}
                                            {clusteringAvailableSeries?.map(
                                                s => (
                                                    <MenuItem
                                                        key={s.id}
                                                        value={s.id}
                                                        dense
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            {s.name}
                                                        </Typography>
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                        {indicatorSelected && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    ml: 0.5,
                                                }}
                                            >
                                                {(
                                                    [
                                                        ['sell', 'Sell'],
                                                        ['hold', 'Hold'],
                                                        ['buy', 'Buy'],
                                                    ] as const
                                                )
                                                    // A switch never sits at
                                                    // the midpoint, so it has
                                                    // no "hold" to explain.
                                                    .filter(
                                                        ([k]) =>
                                                            !binaryMode ||
                                                            k !== 'hold'
                                                    )
                                                    .map(([key, label]) => (
                                                        <Box
                                                            key={key}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 0.35,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 9,
                                                                    height: 9,
                                                                    borderRadius: 0.4,
                                                                    bgcolor:
                                                                        indicatorPalette(
                                                                            isDarkMode
                                                                        )[key],
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '10px',
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                {label}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                            </Box>
                                        )}
                                        <Tooltip
                                            title={
                                                binaryMode
                                                    ? 'Binary coloring (good/bad)'
                                                    : 'Full coloring'
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Switch
                                                    checked={binaryMode}
                                                    onChange={e =>
                                                        setBinaryMode(
                                                            e.target.checked
                                                        )
                                                    }
                                                    size="small"
                                                    sx={{ m: 0 }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                )}
                            <RangeBtn
                                options={['1W', '1M', '3M', '1Y', 'ALL']}
                                active={timeRange}
                                onChange={v =>
                                    onTimeRangeChange(v as TimeRange)
                                }
                            />
                            <Select
                                value={frequencyOverride ?? '__auto__'}
                                onChange={e => {
                                    const v = e.target.value;
                                    setFrequencyOverride(
                                        v === '__auto__'
                                            ? null
                                            : (v as CandleFrequency)
                                    );
                                }}
                                size="small"
                                sx={{
                                    height: 26,
                                    fontSize: '11px',
                                    '& .MuiSelect-select': { py: 0, pl: 1 },
                                }}
                            >
                                <MenuItem value="__auto__" dense>
                                    <Typography sx={{ fontSize: '11px' }}>
                                        Auto ({autoFrequency})
                                    </Typography>
                                </MenuItem>
                                {(
                                    [
                                        '1H',
                                        '1D',
                                        '1W',
                                        '1M',
                                    ] as CandleFrequency[]
                                ).map(f => (
                                    <MenuItem key={f} value={f} dense>
                                        <Typography sx={{ fontSize: '11px' }}>
                                            {f}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                            {zoomRange && (
                                <Chip
                                    label="Reset"
                                    size="small"
                                    onDelete={() => onZoomRangeChange?.(null)}
                                    sx={{
                                        height: 22,
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        '& .MuiChip-deleteIcon': {
                                            fontSize: '14px',
                                        },
                                    }}
                                />
                            )}
                            <ExportButton
                                series={exportSeries}
                                filenamePrefix={ticker ?? 'asset'}
                            />
                            <IconButton
                                size="small"
                                onClick={() => setFullscreen(f => !f)}
                                sx={{ p: 0.25, ml: 0.5 }}
                            >
                                {fullscreen ? (
                                    <FullscreenExitIcon sx={{ fontSize: 18 }} />
                                ) : (
                                    <FullscreenIcon sx={{ fontSize: 18 }} />
                                )}
                            </IconButton>
                        </Box>
                    </PanelHeader>
                    {chartArea}
                </SectionCard>
            </Box>
        </>
    );
}
